/* eslint-disable complexity */

import messaging from '@react-native-firebase/messaging';
import { ActivationState, StompSocketState } from '@stomp/stompjs';
import Service from 'react-reservice';

import requestOtpAPI, { IRequestOtpParams } from '@webtrader/core/api/account/request-otp';
import resetPasswordAPI, { IResetPasswordParams } from '@webtrader/core/api/account/resetPassword';
import { ELoginConfigType } from '@webtrader/core/api/config/company';
import { ITradingAccount, IUserProfile } from '@webtrader/core/api/user';
import MT4LoginAPI from '@webtrader/core/api/user/MT4Login';
import MT5LoginAPI from '@webtrader/core/api/user/MT5Login';
import accountAPI, { ISwitchAccountParams, passwordTypeMapping } from '@webtrader/core/api/user/account';
import languageApi from '@webtrader/core/api/user/language';
import loginAPI from '@webtrader/core/api/user/login';
import logoutAPI from '@webtrader/core/api/user/logout';
import omsLogin from '@webtrader/core/api/user/oms-login';
import sessionAPI from '@webtrader/core/api/user/session';
import { ELoginMethod, ELoginSource, ILoginErrorResponse, ILoginResponse } from '@webtrader/core/api/user/types';
import appsFlyerSDK from '@webtrader/core/appsflyer';
import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@webtrader/core/constants/account';
import { checkIsNumber } from '@webtrader/core/format/number';
import i18n, { TLang, changeLanguage, getUserLanguage } from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { IServiceApiBaseResponse } from '@webtrader/core/services';
import { mixpanelAnalysisService } from '@webtrader/core/services/analysis';
import { EMixpanelProperties } from '@webtrader/core/services/analysis/constants';
import { companyService } from '@webtrader/core/services/company';
import { editOrderService } from '@webtrader/core/services/form/order/edit';
import { newOrderService } from '@webtrader/core/services/form/order/new';
import { orderService } from '@webtrader/core/services/order';
import { socketService } from '@webtrader/core/socket';
import storage, { EStorageKey } from '@webtrader/core/storage';
import { broadcastTradingAccountChangeMessage } from '@webtrader/core/syncing/tradingAccount';
import { isEmpty, isPropInvalid } from '@webtrader/core/validators';
import { checkIsNative } from '@webtrader/core/validators/device';

import { alertService } from '../alert';
import { chartService } from '../chart';
import debugLogService from '../debug-log';

export enum ESortFilter {
  ACCOUNT_NUMBER = 'accountNumber',
  BALANCE = 'balance'
}

interface ILoginParams {
  userId: string;
  password: string;
  deviceId?: string;
  browserType?: string;
  deviceType?: string;
  fetchAccounts?: boolean;
  marketingPageUrl?: string;
  language?: string;
  onError?: (message: string) => void;
  onBeforeSuccess?: () => Promise<void>;
  onSuccess?: () => void;
  /**
   * Determines whether or not changeLanguage should be run here.
   * Set to true if user selected language is already set (i.e. no need to change again)
   * @default false
   */
  disableChangeLang?: boolean;
}

interface ILogoutParams {
  loginAsDemo?: boolean;
  loginAsLive?: boolean;
  navigateToSignUpForm?: boolean;
  clearSettings?: boolean;
  onRedirect?: () => void;
  refetchCompanyProfile?: boolean;
}

export interface ITradingAccountBalances
  extends Pick<
    ITradingAccount,
    // togglable
    | 'equity'
    | 'margin'
    | 'balance'
    | 'freeMargin'
    | 'profitLoss'
    | 'notional'
    | 'commission'
    | 'swaps'
    | 'availableWithdraw'
    | 'credit'
    | 'tradingState'
    // Non togglabale
    | 'marginLevel'
    | 'marginCall'
    | 'marginStopout'
  > {}

interface IHandleLoginResultParams {
  result: ILoginResponse | null | undefined;
  userId: string;
  password: string;
  fetchAccounts: boolean;
  onBeforeSuccess?: () => Promise<void>;
  onSuccess?: () => void;
}

interface IAuthContext {
  loginAs: ELoginAs;
  ready: boolean;
  source?: ELoginSource;
  loading: boolean;
  splashLoading: boolean;
  refreshing?: boolean;
  userProfile?: IUserProfile;
  authenticated: boolean;
  switchAccountLoading: boolean;
  selectedTradingAccount?: ITradingAccount;
  selectedTradingAccountBalances?: ITradingAccountBalances;
  accountSortBy?: ESortFilter;
  isAscendingSortOrder: boolean;
  initialContext: boolean;
  navigateToSignUpForm: boolean;
  usernamePrefilled?: number | string;
  passwordPrefilled?: string;
  usernamePrefilledFromStorage?: number | string;
  passwordPrefilledFromStorage?: string;
  lockedUntil?: number;
  loginErrorCode?: ECommonResponseCode;

  // Reset password
  loadingResetPassword: boolean;

  // Request OTP
  loginMethod: ELoginMethod;
  requestOtpLoading: boolean;
}

const defaultContext: IAuthContext = {
  loginAs: ELoginAs.LIVE,
  ready: false,
  loading: false,
  splashLoading: false,
  authenticated: false,
  switchAccountLoading: false,
  isAscendingSortOrder: false,
  initialContext: true,
  navigateToSignUpForm: false,
  usernamePrefilled: undefined,
  passwordPrefilled: undefined,
  lockedUntil: undefined,
  loginErrorCode: undefined,

  // Reset password
  loadingResetPassword: false,

  // Request OTP
  loginMethod: ELoginMethod.PASSWORD,
  requestOtpLoading: false
};

class AuthService extends Service<IAuthContext> {
  private checkingSession = false;
  private sessionInterval?: NodeJS.Timeout;
  private resetPasswordAbortController = new AbortController();

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = async (source: ELoginSource) => {
    this.context.source = source;
    this.context.splashLoading = true;
    await this.fetchAccounts();
    this.context.splashLoading = false;
  };

  // Primary use for refetch user authentication status when network failure
  public reinit = async () => {
    this.context.splashLoading = true;
    await this.fetchAccounts();
    this.context.splashLoading = false;
  };

  public initWithToken = (token: string, onComplete: () => void) => {
    storage.setItem(EStorageKey.TOKEN, token);
    onComplete();
  };

  public setInitialContext = (initial: boolean) => {
    this.context.initialContext = initial;
  };

  public resetLockedUntil = () => {
    this.context.lockedUntil = undefined;
  };

  public resetloginErrorCode = () => {
    this.context.loginErrorCode = undefined;
  };

  public login = async ({
    userId,
    password,
    deviceId,
    deviceType,
    browserType,
    fetchAccounts = true,
    marketingPageUrl,
    language,
    onError,
    onSuccess,
    onBeforeSuccess,
    disableChangeLang = false
  }: ILoginParams): Promise<IServiceApiBaseResponse> => {
    const { source, loginAs, loginMethod } = this.context;
    const { otpEnabled } = companyService.context.company || {};

    if (!source) {
      return {
        message: 'Source not found'
      };
    }

    if (!disableChangeLang) {
      changeLanguage({ language: i18n.language as TLang });
    }

    try {
      this.resetloginErrorCode();
      this.context.loading = true;

      if (loginAs !== ELoginAs.LIVE && !checkIsNumber(+userId)) {
        this.setInteractable();
        onError?.(mapResponseCodeToMessage(ECommonResponseCode.S2T_1001));
        return {
          message: mapResponseCodeToMessage(ECommonResponseCode.S2T_1001)
        };
      }

      const result = await this.getLoginResult({
        source,
        userId,
        password,
        deviceId,
        browserType,
        deviceType,
        loginAs,
        // Only for otpEnabled (CRM login)
        loginType: otpEnabled ? loginMethod : undefined,
        marketingPageUrl,
        language
      });

      return this.handleLoginResult({ result, userId, password, fetchAccounts, onBeforeSuccess, onSuccess });
    } catch (err) {
      const { code, message, result } = err as IApiError;
      let errorCode = code;
      this.setInteractable();

      if (errorCode === ECommonResponseCode.A_1013) {
        const errorResponse: ILoginErrorResponse = result || {};
        this.context.lockedUntil = errorResponse?.lockedUntil;
      }

      if (errorCode === ECommonResponseCode.BAD_REQUEST) {
        errorCode = ECommonResponseCode.A_1006;
      }

      if (errorCode === ECommonResponseCode.AX_1005) {
        this.context.loginErrorCode = errorCode;
      }

      onError?.(mapResponseCodeToMessage(errorCode, message));

      return {
        message: mapResponseCodeToMessage(errorCode, message)
      };
    } finally {
    }
  };

  private async getLoginResult(params: {
    source: ELoginSource;
    userId: string;
    password: string;
    deviceId?: string;
    browserType?: string;
    deviceType?: string;
    loginAs: string;
    loginType?: ELoginMethod;
    marketingPageUrl?: string;
    language?: string;
  }): Promise<ILoginResponse | null | undefined> {
    const { userId, loginAs, loginType, ...restParams } = params;
    const nonCRMParams = { ...restParams, userId: parseInt(userId) };

    if (companyService.context.company?.multiServerEnabled) {
      if (LIVE_LOGINS_WITHOUT_CRM.includes(loginAs as ELoginAs)) {
        return (await omsLogin.liveLogin(nonCRMParams)).result;
      }

      if (DEMO_LOGINS.includes(loginAs as ELoginAs)) {
        return (await omsLogin.demoLogin(nonCRMParams)).result;
      }
    }

    switch (loginAs) {
      case ELoginAs.LIVE:
        return (await loginAPI({ ...restParams, userId, loginType })).result;
      case ELoginAs.MT4_LIVE:
        return (await MT4LoginAPI.liveLogin(nonCRMParams)).result;
      case ELoginAs.MT4_DEMO:
        return (await MT4LoginAPI.demoLogin(nonCRMParams)).result;
      case ELoginAs.MT5_LIVE:
        return (await MT5LoginAPI.liveLogin(nonCRMParams)).result;
      case ELoginAs.MT5_DEMO:
        return (await MT5LoginAPI.demoLogin(nonCRMParams)).result;
      default:
        return null;
    }
  }

  private handleLoginResult = async ({
    result,
    userId,
    password,
    fetchAccounts,
    onBeforeSuccess,
    onSuccess
  }: IHandleLoginResultParams) => {
    if (result) {
      const { token, user } = result;

      const { loginAs } = this.context;

      if (user) {
        mixpanelAnalysisService.user.login(user);
      }

      if (onBeforeSuccess) {
        await onBeforeSuccess();
      }

      if (!DEMO_LOGINS.includes(loginAs)) {
        storage.setItem(EStorageKey.USER_ID, userId);
        storage.setItem(EStorageKey.PASSWORD, password);
      }

      storage.setItem(EStorageKey.TOKEN, token);

      // To fetch feature announcements after login
      storage.setItem(EStorageKey.SHOULD_FETCH_FEATURE_ANNOUNCEMENTS_API, 'true');

      if (fetchAccounts) {
        await this.fetchAccounts({ onSuccess });
      } else {
        this.setInteractable();
        onSuccess?.();
      }

      return {
        success: true,
        message: i18n.t('Success')
      };
    }

    return {
      message: i18n.t('Unexpected error')
    };
  };

  public getLiveLoginTypeFromCompanyAccess = ({
    mt5LiveLoginConfig,
    mt4LiveLoginConfig
  }: {
    mt5LiveLoginConfig: boolean;
    mt4LiveLoginConfig: boolean;
  }) => {
    if (mt5LiveLoginConfig && mt4LiveLoginConfig) {
      return ELoginAs.OMS_LIVE;
    }
    if (mt4LiveLoginConfig) {
      return ELoginAs.MT4_LIVE;
    }
    if (mt5LiveLoginConfig) {
      return ELoginAs.MT5_LIVE;
    }
    return null;
  };

  public getDemoLoginTypeFromCompanyAccess = ({
    mt5DemoLoginConfig,
    mt4DemoLoginConfig
  }: {
    mt5DemoLoginConfig: boolean;
    mt4DemoLoginConfig: boolean;
  }) => {
    if (mt5DemoLoginConfig && mt4DemoLoginConfig) {
      return ELoginAs.OMS_DEMO;
    }
    if (mt4DemoLoginConfig) {
      return ELoginAs.MT4_DEMO;
    }
    if (mt5DemoLoginConfig) {
      return ELoginAs.MT5_DEMO;
    }
    return null;
  };

  private updateLiveDemoLoginContext = ({
    loginAsDemo,
    loginAsLive
  }: {
    loginAsDemo: boolean;
    loginAsLive: boolean;
  }) => {
    const { mt5DemoLoginConfig, mt4DemoLoginConfig } = companyService.context.access;
    const demoLoginType =
      this.getDemoLoginTypeFromCompanyAccess({ mt5DemoLoginConfig, mt4DemoLoginConfig }) ?? ELoginAs.OMS_DEMO;

    // Switch from Live to Demo Account
    if (loginAsDemo) {
      this.setLoginAs(demoLoginType);
      this.setInitialContext(false);
      return;
    }

    // Switch from Demo to Live Account
    if (loginAsLive) {
      this.setInitialContext(true);
      return;
    }

    // Login out without switching account type
    const isCurrentlyLoginAsDemo = DEMO_LOGINS.includes(this.context.loginAs);

    if (isCurrentlyLoginAsDemo) {
      this.setLoginAs(demoLoginType);
    }
    this.setInitialContext(false);
  };

  public logout = async (params?: ILogoutParams) => {
    const {
      loginAsDemo = false,
      loginAsLive = false,
      navigateToSignUpForm = false,
      clearSettings = true,
      onRedirect,
      refetchCompanyProfile
    } = params || {};

    try {
      mixpanelAnalysisService.user.logout(false);
      await logoutAPI();
    } finally {
      this.stopSessionInterval();

      appsFlyerSDK.clearDeepLinkData();

      socketService.disconnect();

      this.setUserProfile(undefined);

      this.presetTextFields();

      newOrderService.reset();
      editOrderService.reset();

      orderService.clearShowAllSymbols();
      chartService.resetToDefaultContext();

      onRedirect?.();

      this.context.authenticated = false;
      this.context.navigateToSignUpForm = navigateToSignUpForm;

      this.updateLiveDemoLoginContext({ loginAsDemo, loginAsLive });

      if (clearSettings) {
        this.clearPartialSettings();
      }

      if (refetchCompanyProfile) {
        companyService.retrieveCompanyProfile().then((company) => {
          const { defaultLanguage, supportedLanguages } = company || {};
          const userLanguage = getUserLanguage();
          const isUnsupportedLanguage =
            userLanguage && supportedLanguages && !supportedLanguages.includes(userLanguage);
          const languageToBeUpdated = isUnsupportedLanguage ? defaultLanguage : userLanguage;
          changeLanguage({ language: languageToBeUpdated ?? 'en_GB' });
        });
      }

      return {
        success: true,
        message: i18n.t('Success')
      };
    }
  };

  public requestOtp = async (params: IRequestOtpParams) => {
    this.context.requestOtpLoading = true;

    try {
      const { result } = await requestOtpAPI(params);

      if (result) {
        return {
          success: true,
          message: i18n.t('Verification code has been sent successfully. Please check your email or SMS.'),
          data: result
        };
      }

      return {
        message: i18n.t('Unexpected error')
      };
    } catch (err) {
      const { code, message } = err as IApiError;

      return {
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      this.context.requestOtpLoading = false;
    }
  };

  public onSetAccountSortBy = (accountSortBy?: ESortFilter) => {
    this.context.accountSortBy = accountSortBy;
    this.setUserProfile(this.context.userProfile);
  };

  public onSetIsAscendingSortOrder = () => {
    this.context.isAscendingSortOrder = !this.context.isAscendingSortOrder;
    this.setUserProfile(this.context.userProfile);
  };

  public refetch = async () => {
    try {
      this.context.loading = true;

      const { result: updatedUserProfile } = await accountAPI.get();

      this.setUserProfile(updatedUserProfile || undefined);
      if (this.context.selectedTradingAccount) {
        this.updateSelectedTradingAccount();
      }
    } finally {
      this.context.loading = false;
    }
  };

  public setLoginAs = (loginAs: ELoginAs) => {
    this.context.loginAs = loginAs;
  };

  public setLoginMethod = (method: ELoginMethod) => {
    this.context.loginMethod = method;
  };

  public nativeSwitchTradingAccount = async (params: {
    tradingAccount: ITradingAccount;
    onSuccess: (success: boolean, message: string, param?: ECommonResponseCode) => void;
  }): Promise<IServiceApiBaseResponse> => {
    const { tradingAccount, onSuccess } = params;
    const onComplete = async (success: boolean, message: string, param?: ECommonResponseCode) => {
      if (success) {
        await new Promise<boolean>((resolve) => {
          socketService.disconnect(async () => {
            await this.fetchAccounts({ isSwitchAccount: true });

            resolve(true);
          });
        });
      }
      this.context.switchAccountLoading = false;
      onSuccess(success, message, param);
    };
    try {
      this.context.switchAccountLoading = true;
      return this.switchTradingAccount(tradingAccount, false, onComplete);
    } catch (e) {
      this.context.switchAccountLoading = false;
      this.logout({ refetchCompanyProfile: true });

      return {
        message: i18n.t('Unexpected error')
      };
    }
  };

  public switchTradingAccount = async (
    tradingAccount: ITradingAccount,
    updateContext = true,
    onComplete?: (success: boolean, message: string, code?: ECommonResponseCode) => void
  ): Promise<IServiceApiBaseResponse> => {
    const { selectedTradingAccount } = this.context;
    const loginAs = this.context.loginAs;
    const { accountNumber, serverId, selected, metatraderId, simple2TradeAccountType, userGroup } = tradingAccount;
    if (
      accountNumber === selectedTradingAccount?.accountNumber &&
      serverId === selectedTradingAccount?.serverId &&
      selected
    ) {
      return {
        message: i18n.t('Same account')
      };
    }

    const multiServerEnabled = companyService.context.company?.multiServerEnabled ?? false;

    try {
      this.context.switchAccountLoading = true;
      const switchAccountParams: ISwitchAccountParams = {
        metatraderId: metatraderId,
        ...(multiServerEnabled && { serverId }),
        ...(loginAs !== ELoginAs.LIVE && {
          passwordType: passwordTypeMapping(simple2TradeAccountType)
        })
      };

      const { result } = await accountAPI.switch(switchAccountParams);

      if (result) {
        mixpanelAnalysisService.user.switchAccount(tradingAccount);
        if (updateContext) {
          this.context.selectedTradingAccount = tradingAccount;

          orderService.initShowAllSymbols(tradingAccount.accountId);

          this.updateAccountBalances(this.extractBalanceFields(tradingAccount));
        }
        storage.setItem(
          EStorageKey.TRADING_ACCOUNT_IDENTITY,
          JSON.stringify({
            metatraderId: tradingAccount.accountNumber,
            serverId: tradingAccount.serverId
          })
        );
        broadcastTradingAccountChangeMessage({
          metatraderId: tradingAccount.accountNumber,
          serverId: tradingAccount.serverId
        });
        storage.setItem(EStorageKey.META_TRADER_GROUP, userGroup);
        if (result.switchedAccountToken) {
          storage.setItem(EStorageKey.TOKEN, result.switchedAccountToken);
        }
        onComplete?.(true, i18n.t('Success'));
        return {
          success: true,
          message: i18n.t('Success')
        };
      }

      return {
        message: i18n.t('Unexpected error')
      };
    } catch (err) {
      const { code, message } = err as IApiError;
      this.context.switchAccountLoading = false;
      onComplete?.(false, mapResponseCodeToMessage(code, message), code);

      return {
        message: mapResponseCodeToMessage(code, message),
        params: code
      };
    }
  };

  public updateAccountBalances = (params: ITradingAccountBalances) => {
    const { userProfile, selectedTradingAccount, selectedTradingAccountBalances } = this.context;

    if (selectedTradingAccount) {
      this.context.selectedTradingAccountBalances = {
        ...selectedTradingAccountBalances,
        ...params
      };
    }

    if (userProfile && selectedTradingAccount) {
      this.setUserProfile({
        ...userProfile,
        tradingAccounts: userProfile.tradingAccounts?.map((acc) => {
          if (acc.accountId === selectedTradingAccount.accountId && acc.serverId === selectedTradingAccount.serverId) {
            return {
              ...acc,
              ...params
            };
          }

          return acc;
        })
      });
    }
  };

  public resetNavigateToSignUpForm = () => {
    this.context.navigateToSignUpForm = false;
  };

  public resetWhenRootUnmounted = () => {
    this.context.splashLoading = true;
  };

  private setUserProfile = (user?: IUserProfile) => {
    if (!user) {
      this.context.userProfile = undefined;
    } else {
      this.context.userProfile = {
        ...user,
        tradingAccounts: user.tradingAccounts
      };
    }
  };

  private updateSelectedTradingAccount = (tradingAccounts?: ITradingAccount[]) => {
    const accountList = tradingAccounts || this.context.userProfile?.tradingAccounts;
    const currentAccount = accountList?.find(
      (account) => account.accountId === this.context.selectedTradingAccount?.accountId
    );
    if (currentAccount?.language) {
      changeLanguage({ language: currentAccount.language });
    }
    this.context.selectedTradingAccount = currentAccount;

    if (currentAccount) {
      orderService.initShowAllSymbols(currentAccount.accountId);
    }

    this.updateAccountBalances(this.extractBalanceFields(currentAccount || {}));
  };

  private fetchAccount = async () => {
    try {
      return await accountAPI.get();
    } catch (e) {
      // When account api error
      const { code, message } = e as IApiError;
      if (code !== ECommonResponseCode.UNAUTHORIZED) {
        alertService.showAlert({
          message: mapResponseCodeToMessage(code, message)
        });
      }
      return undefined;
    }
  };

  private updateLoginAs = () => {
    const loginAs = this.getLoginAs();
    mixpanelAnalysisService.superProperties.registerSuperProperties({
      [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs
    });
    this.context.loginAs = loginAs;
  };

  public fetchAccounts = async (param?: { onSuccess?: () => void; isSwitchAccount?: boolean }) => {
    const { onSuccess, isSwitchAccount } = param || {};
    try {
      const accountApiResponse = await this.fetchAccount();

      const { result: accountAPIResult } = accountApiResponse || {};

      if (accountAPIResult) {
        this.updateUserAndSelectDefault(accountAPIResult, isSwitchAccount);
        if (checkIsNative()) {
          // Subscribe to Firebase Cloud Messaging Topics for Android/iOS session logins
          this.subscribeFromFCMTopics();
        }
        await new Promise<boolean>((resolve) => {
          const onSocketSuccess = async () => {
            this.context.authenticated = !!this.context.userProfile;
            this.setInteractable();
            this.startSessionInterval();
            onSuccess?.();
            resolve(true);
          };
          if (socketService.state() === ActivationState.ACTIVE) {
            onSocketSuccess();
          } else {
            socketService.connect((state) => {
              if (state === StompSocketState.OPEN) {
                onSocketSuccess();
              }
            });
          }
        });
        debugLogService.log('Account', 'success');
      } else {
        this.setInteractable();
        // Clear all Firebase Cloud Messaging Topics if session is timed out
        if (checkIsNative()) {
          this.unsubscribeFromFCMTopics();
        }
      }
    } catch (e) {
      this.setInteractable();
      // Clear all Firebase Cloud Messaging Topics if session is timed out
      if (checkIsNative()) {
        this.unsubscribeFromFCMTopics();
      }
      debugLogService.log('Account', 'error', e);
    }
  };

  public fetchAccountBalance = async () => {
    try {
      const { result } = await accountAPI.get();

      if (result?.tradingAccounts.length) {
        const selectedTradingAccount = result?.tradingAccounts.find((ta) => ta.selected) || result?.tradingAccounts[0];
        this.updateAccountBalances(this.extractBalanceFields(selectedTradingAccount || {}));
      }
    } catch (e) {}
  };

  /** Private methods */

  // Helper method to extract balance fields from trading account
  private extractBalanceFields = (account: Partial<ITradingAccount>): ITradingAccountBalances => {
    const fields: (keyof ITradingAccountBalances)[] = [
      'equity',
      'margin',
      'balance',
      'freeMargin',
      'profitLoss',
      'marginLevel',
      'marginCall',
      'marginStopout',
      'notional',
      'commission',
      'swaps',
      'availableWithdraw',
      'credit',
      'tradingState'
    ];
    return Object.fromEntries(fields.map((field) => [field, account[field]])) as ITradingAccountBalances;
  };

  private updateUserAndSelectDefault = (user?: IUserProfile, isSwitchAccount?: boolean) => {
    const { tradingAccounts = [] } = user || {};

    if (tradingAccounts.length > 0) {
      const selectedTradingAccount = tradingAccounts.find((ta) => ta.selected) || tradingAccounts[0];

      if (selectedTradingAccount) {
        storage.setItem(
          EStorageKey.TRADING_ACCOUNT_IDENTITY,
          JSON.stringify({
            metatraderId: selectedTradingAccount.metatraderId,
            serverId: selectedTradingAccount.serverId
          })
        );
        broadcastTradingAccountChangeMessage({
          metatraderId: selectedTradingAccount.accountNumber,
          serverId: selectedTradingAccount.serverId
        });
        storage.setItem(EStorageKey.META_TRADER_GROUP, selectedTradingAccount.userGroup);
        storage.setItem(EStorageKey.TRADE_SUBSCRIPTION, selectedTradingAccount.traderSubscription);
        storage.setItem(EStorageKey.MARKET_SUBSCRIPTION, selectedTradingAccount.marketSubscription);

        if (selectedTradingAccount?.metatraderId) {
          mixpanelAnalysisService.user.identifyUser(selectedTradingAccount);
        }

        this.setUserProfile(user);
        if (!isSwitchAccount) {
          this.updateLoginAs();
        }
        if (selectedTradingAccount?.language) {
          changeLanguage({ language: selectedTradingAccount.language });
        }
        this.context.selectedTradingAccount = selectedTradingAccount;

        orderService.initShowAllSymbols(selectedTradingAccount.accountId);

        this.updateAccountBalances(this.extractBalanceFields(selectedTradingAccount || {}));
      }
    }
  };

  private setInteractable = () => {
    this.context.ready = true;
    this.context.loading = false;
    this.context.switchAccountLoading = false;
  };

  private startSessionInterval = () => {
    if (this.sessionInterval) {
      clearInterval(this.sessionInterval);
    }
    this.sessionInterval = setInterval(() => {
      this.checkSession();
    }, 5000);

    // NOTE: To remove after release-2.9, this is deprecated and replaced by initialising the token through AppDeletegate.mm
    // Reference: https://github.com/invertase/react-native-firebase/issues/6893#issuecomment-1873750889
    // if (checkIsNative()) {
    //   messaging().registerDeviceForRemoteMessages();
    // }
  };

  private stopSessionInterval = () => {
    const { sessionInterval } = this;

    if (sessionInterval) {
      clearInterval(sessionInterval);
    }

    if (checkIsNative()) {
      this.unsubscribeFromFCMTopics();
    }
  };

  private clearPartialSettings = () => {
    storage.setItem(EStorageKey.TOKEN, '');
    storage.setItem(EStorageKey.TRADING_ACCOUNT_IDENTITY, null);
    storage.setItem(EStorageKey.META_TRADER_GROUP, '');
    storage.setItem(EStorageKey.SEARCH_HISTORY, []);
  };

  private checkSession = async () => {
    const { context, checkingSession } = this;
    const { authenticated } = context;

    if (authenticated && !checkingSession) {
      this.checkingSession = true;

      try {
        const { code } = await sessionAPI();

        if (code === ECommonResponseCode.UNAUTHORIZED) {
          this.logout({ refetchCompanyProfile: true });
        }
      } catch (error) {
        const { status, code } = error as IApiError;

        if (
          status?.toString() === ECommonResponseCode.UNAUTHORIZED ||
          // Tenant deactivated by root admin: status 400 + code C_MT_0001 (Invalid Tenant Setup)
          (code === ECommonResponseCode.C_MT_0001 && status?.toString() === ECommonResponseCode.BAD_REQUEST)
        ) {
          this.logout({ refetchCompanyProfile: true });
        }
      }

      this.checkingSession = false;
    }
  };

  // Android/iOS ONLY method: Subscribing of Firebase Messaging Topics when user logs in new device session
  private subscribeFromFCMTopics = async () => {
    const { notificationTopics } = this.context.userProfile || {};

    if (!notificationTopics || notificationTopics.length === 0) {
      return;
    }

    await Promise.all(
      notificationTopics.map(async (topic) => {
        try {
          await messaging().subscribeToTopic(topic);
          this.updateSubscribedFCMTopics(topic, true);
        } catch (error) {
          // Intended DEV message
          // eslint-disable-next-line no-console
          console.error(`Failed to subscribe to FCM topic "${topic}":`, error);
        }
      })
    );
  };

  // Android/iOS ONLY method: Unsubscribing of Firebase Messaging Topics when device session is invalid
  private unsubscribeFromFCMTopics = async () => {
    const storedFCMTopics = storage.getItem(EStorageKey.FCM_TOPICS) as string;
    const subscribedFCMTopics = storedFCMTopics?.split(',');

    if (!isEmpty(storedFCMTopics)) {
      await Promise.all(
        subscribedFCMTopics.map(async (topic) => {
          try {
            messaging().unsubscribeFromTopic(topic);
            this.updateSubscribedFCMTopics(topic, false);
          } catch (error) {
            // Intended DEV message
            // eslint-disable-next-line no-console
            console.error(`Failed to unsubscribe to FCM topic "${topic}":`, error);
          }
        })
      );

      await messaging().deleteToken();
    }
  };

  private updateSubscribedFCMTopics = (topicToUpdate: string, subscribe: boolean) => {
    const storedFCMTopicString = (storage.getItem(EStorageKey.FCM_TOPICS) as string) || '';
    const subscribedFCMTopics = storedFCMTopicString?.split(',');

    let updatedFCMTopics = storedFCMTopicString;

    const isTopicSubscribed = subscribedFCMTopics.find((subscribedTopic) => subscribedTopic === topicToUpdate);

    if (subscribe) {
      if (isPropInvalid(isTopicSubscribed) && !isEmpty(storedFCMTopicString)) {
        // Add a new topic on top of existing topic list
        updatedFCMTopics = subscribedFCMTopics.join(',') + `,${topicToUpdate}`;
      } else if (isEmpty(storedFCMTopicString)) {
        // Add a new topic on top of empty topic list
        updatedFCMTopics = topicToUpdate;
      }
    } else if (!subscribe) {
      if (!isPropInvalid(isTopicSubscribed)) {
        updatedFCMTopics = subscribedFCMTopics.filter((subscribedTopic) => subscribedTopic !== topicToUpdate).join(',');
      }
    }

    storage.setItem(EStorageKey.FCM_TOPICS, updatedFCMTopics);
  };

  public presetTextFields = () => {
    const rememberMe = storage.getItem(EStorageKey.REMEMBER_ME) as boolean;
    const storageUserId = storage.getItem(EStorageKey.USER_ID) as string;
    const storagePassword = storage.getItem(EStorageKey.PASSWORD) as string;
    this.setPrefilledValueLiveAccount(rememberMe ? storageUserId : undefined, rememberMe ? storagePassword : undefined);
  };

  public setPrefilledValueDemoAccount = (username?: number | string, password?: string) => {
    this.context.usernamePrefilled = username;
    this.context.passwordPrefilled = password;
  };

  public setPrefilledValueLiveAccount = (username?: number | string, password?: string) => {
    this.context.usernamePrefilledFromStorage = username;
    this.context.passwordPrefilledFromStorage = password;
  };

  public updateAccountLanguage = async (onError?: (message: string) => void) => {
    try {
      await languageApi.updateLanguage();
    } catch (error) {
      const { code, message } = error as IApiError;
      onError?.(mapResponseCodeToMessage(code, message));
    }
  };

  public resetPassword = async (params: IResetPasswordParams): Promise<IServiceApiBaseResponse> => {
    this.context.loadingResetPassword = true;

    try {
      this.resetPasswordAbortController.abort();
      this.resetPasswordAbortController = new AbortController();

      const { result } = await resetPasswordAPI(params, this.resetPasswordAbortController.signal);

      if (result) {
        return {
          success: true,
          message: 'Success'
        };
      }

      return { message: i18n.t('Unexpected error') };
    } catch (err) {
      const { code, message } = err as IApiError;

      return { message: mapResponseCodeToMessage(code, message) };
    } finally {
      this.context.loadingResetPassword = false;
    }
  };

  private getLoginAs = () => {
    const { loginConfig } = this.context.userProfile || {};

    switch (loginConfig) {
      case ELoginConfigType.COMPANY:
        return ELoginAs.LIVE;
      case ELoginConfigType.METATRADER4_DEMO:
        return ELoginAs.MT4_DEMO;
      case ELoginConfigType.METATRADER4_LIVE:
        return ELoginAs.MT4_LIVE;
      case ELoginConfigType.METATRADER5_DEMO:
        return ELoginAs.MT5_DEMO;
      case ELoginConfigType.METATRADER5_LIVE:
        return ELoginAs.MT5_LIVE;
      default:
        return ELoginAs.LIVE;
    }
  };
}

export default new AuthService();
